import React, { useState } from "react"
import {
  X,
  CheckCircle,
  XCircle,
  Star,
  AlertCircle,
  Loader,
  RotateCcw,
  Check,
} from "lucide-react"

// ── Shared style tokens (matching OrgScheduleInterviewForm) ──────────────────
const labelClass =
  "block text-[11px] font-bold text-zinc-400 uppercase tracking-widest mb-1.5"

const InterviewerFeedbackModal = ({
  isOpen,
  onClose,
  initialFeedback = "",
  initialDecision = null,
  onSave,
  isSaving = false,
}) => {
  const [interviewerFeedback, setInterviewerFeedback] =
    useState(initialFeedback)
  const [hiringDecision, setHiringDecision] = useState(initialDecision)

  const handleSave = () => {
    if (!hiringDecision) return
    onSave(hiringDecision, interviewerFeedback)
  }

  const handleReset = () => {
    setHiringDecision(null)
    setInterviewerFeedback("")
  }

  if (!isOpen) return null

  const decisions = [
    {
      value: "no_hire",
      label: "No Hire",
      sub: "Does not meet requirements",
      icon: XCircle,
      active: "border-red-400 bg-red-50",
      iconActive: "text-red-500",
      dotActive: "bg-red-500",
      titleActive: "text-red-900",
      subActive: "text-red-500",
    },
    {
      value: "hire",
      label: "Hire",
      sub: "Meets requirements",
      icon: CheckCircle,
      active: "border-green-400 bg-green-50",
      iconActive: "text-green-500",
      dotActive: "bg-green-500",
      titleActive: "text-green-900",
      subActive: "text-green-500",
    },
    {
      value: "strong_hire",
      label: "Strong Hire",
      sub: "Exceeds expectations",
      icon: Star,
      active: "border-indigo-400 bg-indigo-50",
      iconActive: "text-indigo-500 fill-indigo-500",
      dotActive: "bg-indigo-500",
      titleActive: "text-indigo-900",
      subActive: "text-indigo-500",
    },
  ]

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');`}</style>

      <div
        className="bg-white w-full max-w-xl border border-zinc-200 shadow-2xl flex flex-col max-h-[92vh]"
        style={{ borderRadius: 0 }}
      >
        {/* ── Header ── */}
        <div className="flex justify-between items-center px-7 py-4 border-b border-zinc-200 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-indigo-500" />
            <h2 className="text-[13px] font-bold uppercase tracking-widest text-zinc-900">
              Submit Interviewer Feedback
            </h2>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-7 h-7 border border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50 text-zinc-400 hover:text-zinc-700 transition-all"
            style={{ borderRadius: 0 }}
          >
            <X size={14} />
          </button>
        </div>

        {/* ── Content ── */}
        <div className="flex-1 overflow-y-auto px-7 py-5 space-y-6">
          {/* ── Hiring Recommendation ── */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className={labelClass}>Hiring Recommendation</p>
              <span
                className="text-[10px] font-bold uppercase tracking-widest text-red-500 border border-red-200 bg-red-50 px-2 py-0.5"
                style={{ borderRadius: 0 }}
              >
                Required
              </span>
            </div>

            {/* Decision cards */}
            <div
              className="grid grid-cols-3 gap-0 border border-zinc-200"
              style={{ borderRadius: 0 }}
            >
              {decisions.map((d, idx) => {
                const isSelected = hiringDecision === d.value
                const Icon = d.icon
                return (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => setHiringDecision(d.value)}
                    className={`p-4 text-left transition-all border-r last:border-r-0 border-zinc-200 ${
                      isSelected ? d.active : "bg-white hover:bg-zinc-50"
                    }`}
                    style={{ borderRadius: 0 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Icon
                        size={18}
                        className={isSelected ? d.iconActive : "text-zinc-300"}
                      />
                      {isSelected && (
                        <div
                          className={`w-3.5 h-3.5 flex items-center justify-center ${d.dotActive}`}
                          style={{ borderRadius: 0 }}
                        >
                          <Check
                            size={9}
                            className="text-white"
                            strokeWidth={3}
                          />
                        </div>
                      )}
                    </div>
                    <p
                      className={`text-[12px] font-bold mb-0.5 ${
                        isSelected ? d.titleActive : "text-zinc-800"
                      }`}
                    >
                      {d.label}
                    </p>
                    <p
                      className={`text-[11px] ${
                        isSelected ? d.subActive : "text-zinc-400"
                      }`}
                    >
                      {d.sub}
                    </p>
                  </button>
                )
              })}
            </div>

            {/* Validation nudge */}
            {!hiringDecision && (
              <div
                className="mt-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 text-amber-700 text-[11px] font-medium"
                style={{ borderRadius: 0 }}
              >
                <AlertCircle size={12} className="flex-shrink-0" />
                Please select a hiring recommendation
              </div>
            )}
          </div>

          {/* ── Divider ── */}
          <div className="border-t border-zinc-100" />

          {/* ── Comments ── */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className={labelClass} style={{ marginBottom: 0 }}>
                Comments & Observations
              </label>
              <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                Optional
              </span>
            </div>
            <textarea
              value={interviewerFeedback}
              onChange={(e) => setInterviewerFeedback(e.target.value)}
              placeholder={`Share your detailed thoughts:\n• Technical skills and problem-solving ability\n• Communication and collaboration style\n• Cultural fit and team dynamics\n• Areas of strength and improvement`}
              className="w-full mt-1.5 px-3 py-2.5 border border-zinc-200 text-[15px] font-medium text-zinc-800 placeholder-zinc-400 focus:outline-none focus:border-zinc-500 transition-colors bg-white hover:border-zinc-300 resize-none"
              style={{ borderRadius: 0 }}
              rows={8}
            />
            <p
              className="mt-1 text-[11px] text-zinc-400"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              {interviewerFeedback.length} characters
            </p>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="px-7 py-4 border-t border-zinc-200 flex items-center justify-between shrink-0 bg-zinc-50">
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-zinc-400 hover:text-zinc-700 transition-colors"
          >
            <RotateCcw size={11} strokeWidth={2.5} />
            Reset
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-zinc-600 border border-zinc-200 hover:border-zinc-400 hover:text-zinc-900 transition-colors"
              style={{ borderRadius: 0 }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !hiringDecision}
              className="flex items-center gap-2 px-5 py-2 text-[11px] font-bold uppercase tracking-widest bg-zinc-900 text-white hover:bg-zinc-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed border border-zinc-900"
              style={{ borderRadius: 0 }}
            >
              {isSaving ? (
                <>
                  <Loader size={12} className="animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <CheckCircle size={12} strokeWidth={2.5} />
                  Save Feedback
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default InterviewerFeedbackModal
